import BigNumber from "bignumber.js";
import config from "../config";
import {config as wagmiConfig }from "../../wagmi.config";
import useLiquidity from "./useLiquidityHelpers";
import {
  LiquidityPool,
} from "../interfaces/token";
import {
  writeContract,
  waitForTransactionReceipt,
} from "@wagmi/core";
import { useParams } from "react-router-dom";
import { ethers } from "ethers";
import ERC20 from "../../src/assets/abi/farm/ERC20.json";

export interface MapIDofReserves extends LiquidityPool {
  reserves: Record<string, BigNumber>;
  ratios: Record<string, BigNumber>;
}

// Keep track of already logged missing farm pools to reduce spam
const loggedSkippedPairs = new Set<string>();

// Module-level cache for external price API with 60 second TTL
const externalPriceCache: Record<string, { data: any; timestamp: number }> = {};
const CACHE_TTL = 60000; // 60 seconds cache

const useFarmCommon = () => {
  const { getListReserveInPool, getToken0AndToken1InLp } = useLiquidity();
  const { chains } = useParams<{ chains: string }>();

  const getChainId = (code: string) => {
    const { networks } = config;
    const result = Object.keys(networks).find((o) => {
      return networks[Number(o)].code.toLowerCase() === code.toLowerCase();
    });
    return result;
  };

  const chainId = () => {
    if (!chains) return;
    return getChainId(chains);
  };

  const getMapIDofReserves = async (pairIds: string[]) => {
    const reservePools = await getListReserveInPool(pairIds);
    const mapIDofReserves: Record<string, MapIDofReserves> = {};
    await Promise.all(
      reservePools.map(async (reservePool, idx) => {
        let pair = config.lp[pairIds[idx]];
        if (pair === undefined) {
          pair = config.dmmLP[pairIds[idx]];
        }
        if (!pair.address || !reservePool || reservePool === '0') {
          // Only log missing pair once to reduce console spam
          if (!loggedSkippedPairs.has(pairIds[idx])) {
            loggedSkippedPairs.add(pairIds[idx]);
          }
          return {};
        }
        // reservePool is an array: [reserve0, reserve1, timestamp]
        const reserve0 = BigNumber((reservePool as any)[0]?.toString() || '0');
        const reserve1 = BigNumber((reservePool as any)[1]?.toString() || '0');
        const ratios: Record<string, BigNumber> = {};
        const reserves: Record<string, BigNumber> = {};
        const tokens = await getToken0AndToken1InLp(pair.code);

        if (tokens.token0 && tokens.token1) {
          // Store raw reserves
          reserves[tokens.token0.code] = reserve0;
          reserves[tokens.token1.code] = reserve1;
          
          // Calculate price ratios: how much 1 token is worth in terms of the other
          // If BNB/BUSD pair: reserve0=BNB, reserve1=BUSD
          // Price of BNB in BUSD = reserve1/reserve0 (BUSD per BNB)
          // Price of BUSD in BNB = reserve0/reserve1 (BNB per BUSD) 
          if (reserve0.gt(0) && reserve1.gt(0)) {
            ratios[tokens.token0.code] = reserve1.div(reserve0); // token0 price in token1
            ratios[tokens.token1.code] = reserve0.div(reserve1); // token1 price in token0
          } else {
            ratios[tokens.token0.code] = BigNumber(0);
            ratios[tokens.token1.code] = BigNumber(0);
          }
          
          mapIDofReserves[pair.code] = {
            ...pair,
            reserves,
            ratios,
            token0: tokens.token0,
            token1: tokens.token1,
          };
        }
        return {};
      })
    );

    return mapIDofReserves;
  };

  const getPriceTokenExternalApi = async (token: string) => {
    if (!token) return 0;

    const cacheKey = `price_${token}`;
    const now = Date.now();
    const cached = externalPriceCache[cacheKey];

    // Return cached data if still valid
    if (cached && now - cached.timestamp < CACHE_TTL) {
      const price = cached.data?.market_data?.current_price?.usd || 0;
      return price;
    }

    try {
      const url = config.externalPriceAPI + token;
      const response = await fetch(url);
      
      if (!response.ok) {
        // On rate limit or error, return cached data if available (even if expired)
        if (cached) {
          console.warn(`getPriceTokenExternalApi: Rate limited, using stale cache for ${token}`);
          return cached.data?.market_data?.current_price?.usd || 0;
        }
        console.error(`getPriceTokenExternalApi: HTTP ${response.status} for ${token}`);
        return 0;
      }

      const priceSet = await response.json();
      externalPriceCache[cacheKey] = { data: priceSet, timestamp: now };
      
      const price = priceSet?.market_data?.current_price?.usd || 0;
      return price;
    } catch (e) {
      // On error, return cached data if available
      if (cached) {
        console.warn(`getPriceTokenExternalApi: Error fetching, using stale cache for ${token}`);
        return cached.data?.market_data?.current_price?.usd || 0;
      }
      console.error("getPriceTokenExternalApi", e);
      return 0;
    }
  };

  const getReserveByPairCode = (
    mapIDofReserves: Record<string, MapIDofReserves>,
    symbol1: string,
    symbol2: string
  ) => {
    const pair = 
      mapIDofReserves[`${symbol1}_${symbol2}`] ||
      mapIDofReserves[`${symbol2}_${symbol1}`];
    
    return pair;
  };

  const getPrice = async (
    tokens: Record<string, any>,
    mapIDofReserves: Record<string, MapIDofReserves>
  ) => {
    const tokenPrice: Record<string, number> = {};
    
    // First pass: Set base prices and external prices
    for (const id in tokens) {
      const token = tokens[id];
      
      if (token.externalPrice) {
        const externalPrice = await getPriceTokenExternalApi(token.slug ?? "");
        tokenPrice[token.symbol] = externalPrice;
      } else if (token.fixedPrice) {
        tokenPrice[token.symbol] = token.fixedPrice;
      } else if (!token.priceRoute || token.priceRoute.length === 0) {
        // Base currency (like BUSD)
        tokenPrice[token.symbol] = 1;
      }
    }
    
    // Second pass: Calculate prices using routes
    const maxIterations = 10; // Prevent infinite loops
    let iteration = 0;
    let pendingTokens = Object.keys(tokens).filter(id => {
      const token = tokens[id];
      return token.priceRoute && token.priceRoute.length > 0 && !tokenPrice.hasOwnProperty(token.symbol);
    });
    
    while (pendingTokens.length > 0 && iteration < maxIterations) {
      iteration++;
      
      const initialPendingCount = pendingTokens.length;
      
      for (let i = pendingTokens.length - 1; i >= 0; i--) {
        const id = pendingTokens[i];
        const token = tokens[id];
        let canCalculate = true;
        let targetPrice = 1;
        let currentSymbol = token.symbol;
        
        // Check if we can calculate this token's price
        for (const routeSymbol of token.priceRoute) {
          const reserve = getReserveByPairCode(mapIDofReserves, currentSymbol, routeSymbol);
          
          if (!reserve) {
            canCalculate = false;
            break;
          }
          
          if (!tokenPrice.hasOwnProperty(routeSymbol)) {
            canCalculate = false;
            break;
          }
          
          const currentTokenCode = `${currentSymbol}_TOKEN`;
          const ratio = reserve.ratios[currentTokenCode];
          
          if (!ratio || ratio.isNaN() || !ratio.isFinite() || ratio.eq(0)) {
            canCalculate = false;
            break;
          }
          
          // ratio gives us how much of routeSymbol we get for 1 currentSymbol
          // So currentSymbol price = ratio * routeSymbol price
          const ratioNumber = Number(ratio);
          const routePrice = tokenPrice[routeSymbol];
          targetPrice = targetPrice * ratioNumber * routePrice;
          currentSymbol = routeSymbol;
        }
        
        if (canCalculate) {
          tokenPrice[token.symbol] = targetPrice;
          pendingTokens.splice(i, 1);
        }
      }
      
      // If no progress was made, break to avoid infinite loop
      if (pendingTokens.length === initialPendingCount) {
        break;
      }
    }
    
    return tokenPrice;
  };

  const getLiquidityLink = (staking: any) => {
    if (staking?.lpType) {
      return `/pool/add?tokenA=${staking.token0.code}&tokenB=${staking.token1.code}`;
    } else if (staking?.fixedPrice || staking?.externalPrice) {
      return null;
    } else {
      return `/exchange/swap?tokenB=${staking.code}`;
    }
  };

  const approveToken = async (
    contractAddress: string,
    tokenAddress: string,
    account: string,
    events: any
  ) => {
    try {
      // const id = chainId();
      const hash = await writeContract(wagmiConfig, {
        abi: ERC20,
        address: tokenAddress as `0x${string}`,
        functionName: "approve",
        args: [contractAddress as `0x${string}`, ethers.MaxUint256],
      });
      
       // Trigger transactionHash callback
       if (events.onTransactionHash) events.onTransactionHash(hash);

       // Wait for the transaction to be mined
       const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
 
       // Trigger receipt callback
       if (events.onReceipt) events.onReceipt({transactionHash: receipt.transactionHash, status: receipt.status === "success"});

      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (error) {
      console.error("approveToken", error);
      events.onError(error);
      return { transactionHash: "", status: false };
    }
  };

  return {
    getMapIDofReserves,
    getPriceTokenExternalApi,
    getReserveByPairCode,
    getPrice,
    getLiquidityLink,
    chainId,
    approveToken,
  };
};

export default useFarmCommon;
